/**
 * PRP-WEB-1 W2.2: the dashboard's Columns button: a checklist that shows and hides table
 * columns (TanStack column visibility), remembered per browser. A plain disclosure rather than
 * the Radix dropdown so a click toggles a column without closing the list.
 */
import { useEffect, useRef, useState } from 'react';
import type { VisibilityState } from '@tanstack/react-table';

import { readStored, writeStored } from './DataTable';
import { IconColumns } from './icons';
import { Button } from './ui/button';

export interface ColumnChoice {
  id: string;
  label: string;
}

/** Column visibility kept in localStorage under `key` (best effort; a blocked store just forgets). */
export function usePersistentVisibility(key: string): [VisibilityState, (_v: VisibilityState) => void] {
  const [vis, setVis] = useState<VisibilityState>(() => readStored<VisibilityState>(key) ?? {});
  const set = (v: VisibilityState) => {
    setVis(v);
    writeStored(key, v);
  };
  return [vis, set];
}

export default function ColumnsMenu({
  columns,
  visibility,
  onChange,
}: {
  columns: ColumnChoice[];
  visibility: VisibilityState;
  onChange: (_v: VisibilityState) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shownCount = columns.filter((c) => visibility[c.id] !== false).length;

  return (
    <div className="relative" ref={box}>
      <Button
        variant="secondary"
        size="sm"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        data-testid="columns-button"
      >
        <IconColumns />
        Columns
      </Button>
      {open && (
        <div
          role="group"
          aria-label="Show columns"
          data-testid="columns-menu"
          className="absolute right-0 top-[calc(100%+4px)] z-30 w-[200px] rounded-[var(--r-lg)] border border-[var(--bd)] bg-[var(--bg-2)] p-1.5 shadow-lg"
        >
          {columns.map((c) => {
            const on = visibility[c.id] !== false;
            // The last visible column cannot be hidden: an empty table reads as "no projects".
            const locked = on && shownCount === 1;
            return (
              <label
                key={c.id}
                className="flex h-[30px] cursor-pointer items-center gap-2 rounded-[var(--r-sm)] px-2 text-[12.5px] text-[var(--tx-mid)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)]"
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={locked}
                  onChange={() => onChange({ ...visibility, [c.id]: !on })}
                  className="accent-[var(--accent)]"
                />
                {c.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
